






































import { Component, Vue } from "nuxt-property-decorator";
import MarkdownIt from "markdown-it";
import { getCalendar, createEntry, updateEntry, deleteEntry } from "~/lib/GrpcClient";
import * as JsonApiClient from "~/lib/JsonApiClient";
import { calendarColor } from "~/lib/utils/Colors";
import { Calendar } from "~/types/adventar";
import { getToken } from "~/lib/Auth";
import UserIcon from "~/components/UserIcon.vue";
import CalendarTable from "~/components/CalendarTable.vue";
import EntryList from "~/components/EntryList.vue";

@Component({
  components: { UserIcon, CalendarTable, EntryList }
})
export default class extends Vue {
  calendar: Calendar | null = null;

  head() {
    if (this.calendar === null) return {};

    return {
      title: `${this.title} - Adventar`,
      meta: [
        { hid: "description", name: "description", content: this.calendar.description },
        { hid: "og:description", property: "og:description", content: this.calendar.description },
        { hid: "og:title", property: "og:title", content: `${this.title} - Adventar` }
      ],
      link: [
        { rel: "alternate", type: "application/rss+xml", href: `/calendars/${this.calendar.id}.rss` },
        {
          rel: "alternate",
          type: "application/json+oembed",
          href: `https://adventar.org/oembed?url=${this.embedUrl}`
        }
      ]
    };
  }

  async asyncData({ params, error }) {
    if (process.server) {
      let calendar: Calendar;
      try {
        calendar = await JsonApiClient.getCalendar(params.id);
      } catch (err) {
        if (err.response) {
          error({ statusCode: err.response.status });
        } else {
          console.error(err);
          error({ statusCode: 500 });
        }

        return;
      }
      return { calendar };
    }
  }

  async mounted() {
    const clanedarId = Number(this.$route.params.id);
    if (this.calendar && Number(this.calendar.id) !== clanedarId) {
      this.calendar = null;
    }
    // TODO: 404 if not found
    this.calendar = await getCalendar(clanedarId);
  }

  async refetchCalendar() {
    this.calendar = await getCalendar(this.calendar!.id);
  }

  async handleCreateEntry(day: number) {
    try {
      const token = await getToken();
      const calendarId = this.calendar!.id;
      const entry = await createEntry({ calendarId, day, token });
      await this.refetchCalendar();
      return entry;
    } catch (err) {
      alert(`Request failed: ${err.message}`);
      console.error(err);
    }
  }

  async handleUpdateEntry(entryId: number, { comment, url }: { comment: string; url: string }) {
    try {
      const token = await getToken();
      await updateEntry({ entryId, comment, url, token });
      await this.refetchCalendar();
    } catch (err) {
      alert(`Request failed: ${err.message}`);
      console.error(err);
    }
  }

  async handleDeleteEntry(entryId: number): Promise<void> {
    try {
      const token = await getToken();
      await deleteEntry({ entryId, token });
      this.calendar = await getCalendar(this.calendar!.id);
      await this.refetchCalendar();
    } catch (err) {
      alert(`Request failed: ${err.message}`);
      console.error(err);
    }
  }

  isOwnCalendar(calendar: Calendar): boolean {
    if (!this.$store.state.user) return false;
    if (!calendar.owner) return false;
    return calendar.owner.id === this.$store.state.user.id;
  }

  get calendarColor(): string {
    return calendarColor(this.calendar!.id);
  }

  get title(): string {
    if (this.calendar === null) {
      return "Adventar";
    }
    return `${this.calendar.title} Advent Calendar ${this.calendar.year}`;
  }

  get descriptionHtml(): string {
    if (!this.calendar || !this.calendar.description) return "";
    return MarkdownIt({ linkify: true, breaks: true }).render(this.calendar.description);
  }

  get embedUrl(): string {
    if (this.calendar === null) return "";
    return encodeURIComponent(`https://adventar.org/calendars/${this.calendar.id}`);
  }
}
